/**
 * SSOT for global loading surface show/hide orchestration timing (LoadingSurface fullscreen).
 *
 * ## Semantics
 *
 * These values control **when the global overlay appears/disappears** (UX anti-flicker),
 * not how fast the PageLoader animation spins.
 *
 * | Constant | Role |
 * |----------|------|
 * | `GLOBAL_OVERLAY_SHOW_DELAY_MS` | Debounce show — overlay only appears if `bootstrapping` stays true ≥ this duration |
 * | `GLOBAL_OVERLAY_MIN_VISIBLE_MS` | Min visible — once shown, overlay stays at least this long after `bootstrapping` becomes false |
 *
 * ## Not these fields
 *
 * - **Not** `loader_indicator_duration_ms` — PageLoader animation cycle speed (configurable in UI Settings).
 * - **Not** `config_overview_load_duration_ms` — deprecated orphan DB field; never reuse for overlay timing.
 *
 * ## Configurability
 *
 * Not admin-configurable in Loading-6 (Option A). If product needs per-tenant tuning later, add
 * dedicated fields (e.g. `overlay_show_delay_ms` / `overlay_min_visible_ms`) — do not reuse
 * orphan or animation-duration fields.
 *
 * @see docs/standards/standard-loading.md
 */

/**
 * Debounce before showing the global overlay (ms).
 *
 * UX orchestration constant — **not** animation duration, **not** admin-configurable.
 * If `bootstrapping` becomes false before this delay elapses, the overlay never shows.
 */
export const GLOBAL_OVERLAY_SHOW_DELAY_MS = 150;

/**
 * Minimum time the global overlay stays visible after it has been shown (ms).
 *
 * UX orchestration constant — **not** animation duration, **not** admin-configurable.
 * Applied when `bootstrapping` goes false while overlay is already visible.
 */
export const GLOBAL_OVERLAY_MIN_VISIBLE_MS = 400;

/**
 * Remaining hide delay (ms) given time already visible.
 * Documents the formula used in `LoadingSurface` (`Math.max(0, MIN_VISIBLE - elapsed)`).
 */
export function computeGlobalOverlayHideDelayMs(elapsedVisibleMs: number): number {
  return Math.max(0, GLOBAL_OVERLAY_MIN_VISIBLE_MS - elapsedVisibleMs);
}

/**
 * Whether a bootstrap window shorter than show delay should suppress overlay display.
 * True when bootstrap ended before {@link GLOBAL_OVERLAY_SHOW_DELAY_MS}.
 */
export function shouldSuppressOverlayShow(bootstrapActiveMs: number): boolean {
  return bootstrapActiveMs < GLOBAL_OVERLAY_SHOW_DELAY_MS;
}
